import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const data = await api.getInvoices();
      setInvoices(data);
    } catch (err) {
      setError(err.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = '$') => {
    if (!amount) return `${currency}0.00`;
    return `${currency}${parseFloat(amount).toFixed(2)}`;
  };

  // Filter invoices based on status and search
  const filteredInvoices = invoices.filter((invoice) => {
    const matchesFilter = filter === 'all' || invoice.status === filter;
    const matchesSearch = !searchQuery ||
      invoice.invoice_number?.toString().includes(searchQuery) ||
      invoice.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice.job_title?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: 'bg-slate-100 text-slate-700',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      voided: 'bg-red-100 text-red-800 line-through',
    };
    return statusStyles[status] || 'bg-slate-100 text-slate-700';
  };

  // Get status icon
  const getStatusIcon = (status) => {
    const icons = {
      draft: 'fa-file-alt',
      sent: 'fa-paper-plane',
      paid: 'fa-check-circle',
      overdue: 'fa-exclamation-circle',
      voided: 'fa-ban',
    };
    return icons[status] || 'fa-file-alt';
  };

  // Get counts for filter badges
  const getCounts = () => {
    return {
      all: invoices.length,
      draft: invoices.filter(i => i.status === 'draft').length,
      sent: invoices.filter(i => i.status === 'sent').length,
      paid: invoices.filter(i => i.status === 'paid').length,
      voided: invoices.filter(i => i.status === 'voided').length,
    };
  };

  // Calculate totals
  const getTotals = () => {
    const sent = filteredInvoices
      .filter(i => i.status === 'sent')
      .reduce((sum, i) => sum + parseFloat(i.total_with_tax || i.total || 0), 0);
    const paid = filteredInvoices
      .filter(i => i.status === 'paid')
      .reduce((sum, i) => sum + parseFloat(i.total_with_tax || i.total || 0), 0);
    return { sent, paid };
  };

  const counts = getCounts();
  const totals = getTotals();

  if (loading) {
    return (
      <div className="py-20 px-6 bg-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-12 px-6 bg-background min-h-screen">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-black text-primary mb-2">Invoices</h1>
            <p className="text-slate-600">Track payments and manage billing</p>
          </div>
        </div>

        {/* Summary Cards */}
        {invoices.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Outstanding</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(totals.sent)}</p>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
              <p className="text-sm text-slate-500 mb-1">Collected</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totals.paid)}</p>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <i className="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"></i>
            <input
              type="text"
              placeholder="Search invoices..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-secondary focus:outline-none transition"
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex flex-wrap gap-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'draft', label: 'Draft' },
            { key: 'sent', label: 'Sent' },
            { key: 'paid', label: 'Paid' },
            { key: 'voided', label: 'Voided' },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                filter === key
                  ? 'bg-primary text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:border-primary'
              }`}
            >
              {label}
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                filter === key ? 'bg-white/20' : 'bg-slate-100'
              }`}>
                {counts[key]}
              </span>
            </button>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
            {error}
          </div>
        )}

        {/* Invoices List */}
        {filteredInvoices.length === 0 ? (
          <div className="bg-white p-12 rounded-2xl shadow-sm border border-slate-200 text-center">
            <i className="fas fa-receipt text-6xl text-slate-300 mb-4"></i>
            <h3 className="text-xl font-bold text-slate-700 mb-2">No Invoices Found</h3>
            <p className="text-slate-600 mb-6">
              {invoices.length === 0
                ? 'Create invoices from job details to bill your clients.'
                : 'No invoices match your current filters.'}
            </p>
            {invoices.length > 0 && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilter('all');
                }}
                className="text-secondary font-medium hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredInvoices.map((invoice) => (
              <Link
                key={invoice.id}
                to={`/invoices/${invoice.id}`}
                className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition block"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-bold text-primary">
                        Invoice #{invoice.invoice_number || invoice.id.slice(0, 8)}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(invoice.status)}`}>
                        {invoice.status}
                      </span>
                    </div>
                    {invoice.client_name && (
                      <p className="text-slate-600 mb-1">
                        <i className="fas fa-user mr-2 text-slate-400"></i>
                        {invoice.client_name}
                      </p>
                    )}
                    {invoice.job_title && (
                      <p className="text-sm text-slate-500">
                        <i className="fas fa-briefcase mr-2 text-slate-400"></i>
                        {invoice.job_title}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Amount</p>
                      <p className="text-xl font-bold text-primary">
                        {formatCurrency(invoice.total_with_tax || invoice.total)}
                      </p>
                    </div>
                    {invoice.due_date && (
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Due</p>
                        <p className="text-sm font-medium text-slate-700">
                          {new Date(invoice.due_date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    <i className="fas fa-chevron-right text-slate-400"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Summary */}
        {filteredInvoices.length > 0 && (
          <div className="mt-6 text-center text-slate-600">
            Showing {filteredInvoices.length} of {invoices.length} invoices
          </div>
        )}
      </div>
    </div>
  );
}

export default Invoices;
